import Alpine from 'alpinejs';
import Collapse from '@alpinejs/collapse';
import { initPageMotion } from './js/ui/page-motion';
import sidebar from './js/ui/sidebar';
import supportPinModal from './js/ui/support-pin';
import setNewPassword from './js/ui/set-new-password';
import './js/ui/client-runtime';  // relocated layout inline scripts (skeleton reveal, api-link, ajax nav)
import './js/ui/currency-cart';   // relocated currency switcher + cart badge (both layouts)
import './js/ui/page-scripts';    // relocated small page scripts (carousel, order list nav, pending messages)
import './js/ui/email-index';     // relocated email history preview modal

// ── Alpine.js setup ────────────────────────────────────────────────────────────
Alpine.plugin(Collapse);
Alpine.data('mobileSidebar', sidebar);
Alpine.data('SupportPinModal', supportPinModal);
Alpine.data('setNewPassword', setNewPassword);

// Expose Alpine globally so x-data inline expressions and devtools work.
window.Alpine = Alpine;

Alpine.start();

// ── Shared async-action motion state ─────────────────────────────────────────
// Opt-in with data-submit-motion. Markup supplies idle/loading/success labels,
// while this helper owns disabled and aria-busy state without replacing content.
window.MeroMotion = Object.assign(window.MeroMotion || {}, {
  setSubmitState(button, state) {
    if (!button) return;
    const nextState = ['idle', 'loading', 'success'].includes(state) ? state : 'idle';
    button.dataset.state = nextState;
    button.disabled = nextState !== 'idle';
    button.setAttribute('aria-busy', nextState === 'loading' ? 'true' : 'false');
  },
});

// ── TomSelect — enhance <select> inputs ────────────────────────────────────────
document.addEventListener('DOMContentLoaded', function () {
  // Homepage viewport reveals are progressive enhancement: content remains
  // visible when JavaScript is unavailable or reduced motion is requested.
  function initHomepageReveals() {
    const elements = Array.from(document.querySelectorAll('.ref-home [data-reveal]'));
    if (!elements.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (element) {
        element.classList.add('is-revealed');
      });
      return;
    }

    document.documentElement.classList.add('has-home-reveal');

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.12,
    });

    elements.forEach(function (element) {
      observer.observe(element);
    });
  }

  initHomepageReveals();

  /**
   * Global error handler for unhandled Promise rejections.
   * Delegates to FOSSBilling.message() provided by fossbilling.js.
   */
  window.addEventListener('unhandledrejection', function (event) {
    const err = event.reason;
    let msg = 'An unexpected error occurred';
    if (err && typeof err === 'object') {
      msg = err.message || err.code || msg;
    } else if (typeof err === 'string') {
      msg = err;
    }
    if (typeof FOSSBilling !== 'undefined' && FOSSBilling.message) {
      FOSSBilling.message(msg, 'error');
    }
  });

  initPageMotion();

  // Keep native selects usable while their optional enhancement loads.
  let selectModule;
  async function enhanceSelect(el) {
    if (el.tomselect || el.dataset.enhancing) return;
    el.dataset.enhancing = 'true';
    try {
      selectModule ||= import('tom-select').catch(error => {
        selectModule = null;
        throw error;
      });
      const { default: TomSelect } = await selectModule;
      // Do not replace a native select while the user is interacting with it.
      if (document.activeElement === el) {
        el.addEventListener('blur', () => enhanceSelect(el), { once: true });
        return;
      }
      if (!el.isConnected || el.tomselect) return;
      const options = { maxOptions: null };
      if (el.matches('.js-locale-selector')) {
        const renderLocale = (data, escape) => {
          const flag = data.value ? '<span class="fi fi-' + escape(data.value.toLowerCase().slice(3)) + ' locale-flag"></span>' : '';
          return '<div>' + flag + escape(data.text) + '</div>';
        };
        options.render = { option: renderLocale, item: renderLocale };
      }
      new TomSelect(el, options);
    } catch (error) {
      // A failed optional chunk must not block the native form controls.
      console.warn('Select enhancement unavailable', error);
    } finally {
      delete el.dataset.enhancing;
    }
  }
  const selects = document.querySelectorAll('select.js-locale-selector, select.js-select');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        enhanceSelect(entry.target);
      });
    }, { rootMargin: '200px' });
    selects.forEach(el => {
      observer.observe(el);
      el.addEventListener('focus', () => enhanceSelect(el), { once: true });
    });
  } else {
    selects.forEach(enhanceSelect);
  }
});
